import {
  carregarJson,
  clubePorId,
  salvarJson,
  type Clube,
  type ConcentracaoAtaque,
  type AjusteIntervalo,
  type EstadoJogo,
  type EventoPartida,
  type Funcoes,
  type PlanoSub,
  type Estilo,
  type Formacao,
  type Marcacao,
  type OfertaRecebida,
  type Passe,
  type Posicao,
  type Pressao,
  type Proposta,
  type RodadaJogada,
} from '../core';

import { apagarSave, gravarSave, lerSave } from './armazenamento';
export const CHAVE_PREFS = 'novo-jogo:prefs:v1';

import type { VisaoSala } from '../../server/sala';
import { definirSom, somLigado } from './som';

export type Tela = 'inicio' | 'novo' | 'jogo' | 'partida' | 'online' | 'lobby';

export interface SessaoOnline {
  codigo: string;
  token: string;
  visao: VisaoSala | null;
  fonte: EventSource | null;
  /** temporada*1000 + rodada já vistos neste navegador (para abrir a partida quando a rodada roda) */
  rodadaVista: number | null;
  /** instante local em que a visão chegou (para o cronômetro dos leilões) */
  recebidoEm: number;
  /** número da última visão recebida e cópia intacta dela: base para as diferenças que o servidor manda */
  vid?: number | null;
  base?: unknown;
}
export type Dialogo = 'tabelas' | 'estadio' | 'procura' | 'times' | 'escalar' | 'agenda' | 'opcoes' | 'historia' | 'ofertas' | 'negociacao' | 'juniores' | 'financas' | 'tecnicos' | 'leilao' | 'pausa' | 'treino' | 'pedidos' | 'demissao' | 'convites' | 'conta' | 'loja' | 'ranking';

export interface Negociacao {
  jogadorId: number;
  /** quando o jogador é livre, não há clube vendedor */
  livre: boolean;
  oferta: number;
  resposta: Proposta | null;
}

export interface FiltrosProcura {
  nome: string;
  posicao: Posicao | '';
  lado: 'E' | 'D' | '';
  forcaMin: number;
  forcaMax: number;
  idadeMin: number;
  idadeMax: number;
  car1: number;
  car2: number;
  valor: string;
  livres: boolean;
  /** 'br' = jogadores do jogo; 'todos'; ou o id de uma liga estrangeira (só consulta) */
  campeonato: string;
}

export interface ResultadoProcura {
  jogadorId: number;
  clubeId: number | null;
}

export interface EstadoEscalar {
  formacao: Formacao | 'auto';
  titulares: (number | null)[];
  reservas: number[];
  estilo: Estilo;
  marcacao: Marcacao;
  ataque: ConcentracaoAtaque;
  passe: Passe;
  pressao: Pressao;
  selSlot: number | null;
  selJogador: number | null;
  manter: boolean;
  /** painel da direita: escalação (campo, banco, disponíveis) ou plano de jogo */
  painel: 'time' | 'plano';
  plano: PlanoSub[];
  ajustes: AjusteIntervalo[];
  funcoes: Funcoes;
}

/** Janela de substituições com a partida online pausada. */
export interface EstadoPausa {
  /** identifica a pausa (minuto e prazo) para abrir a janela uma vez só */
  chave: string;
  /** intervalo (todos os técnicos confirmam) em vez de pausa de um técnico */
  intervalo?: boolean;
  /** pausa para hidratação aos 65' (também confirmada por todos os técnicos, com o mesmo comando do intervalo) */
  hidratacao?: boolean;
  /** jogo solo: sem relógio do servidor nem limite de tempo (a rodada é refeita no navegador quando o técnico confirma) */
  local?: boolean;
  minuto: number;
  /** ocupante de cada "unidade" (posição que o jogo tinha quando pausou); as trocas e a formação nova mexem só em `perm` */
  ocupantes: (number | null)[];
  slots: Posicao[];
  banco: number[];
  restantes: number;
  pendentes: { saiId: number; entraId: number }[];
  selSai: number | null;
  /** formação em campo quando pausou (null se não for uma das conhecidas) e a escolhida agora */
  formacaoInicial: Formacao | null;
  formacao: Formacao | null;
  /** unidade (índice de `ocupantes`) que está em cada slot da formação escolhida */
  perm: number[];
  estilo: Estilo;
  marcacao: Marcacao;
  ataque: ConcentracaoAtaque;
  passe: Passe;
  pressao: Pressao;
}

export interface PartidaUi {
  rodada: RodadaJogada;
  /** índice da partida do usuário em rodada.partidas */
  indice: number;
  /** partida da rodada que o usuário abriu para ver os detalhes (clique na lista de jogos); ausente = a dele */
  verIndice?: number;
  minuto: number;
  fase: 'ao-vivo' | 'intervalo' | 'penaltis' | 'fim';
  /** disputa de pênaltis do confronto decidido nesta partida: `reveladas` cobranças já mostradas */
  disputa?: { reveladas: number; etapa: 'tensao' | 'resultado' };
  /** partida online: o minuto vem do relógio do servidor (todos os técnicos veem o mesmo jogo) */
  online?: boolean;
  /** jogo solo: a partida está pausada pelo técnico (o relógio da tela não anda) */
  pausado?: boolean;
  /** o técnico não joga esta rodada (eliminado, por exemplo) e só acompanha os jogos: sem pausa, sem mini-game e sem sons do "meu jogo" */
  espectador?: boolean;
  /** mini-game do pênalti sofrido: canto escolhido para o lance `chave` (minuto:batedor) */
  minigame?: { chave: string; escolha: number };
  /** minuto do relógio da rodada (todos os jogos), para mostrar os outros jogos sem adiantar placares */
  minutoGlobal?: number;
  /** online: andamento fino da partida do técnico (0 a 1, com a fração do minuto em curso), para a barra de progresso correr sem degraus */
  progresso?: number;
  /** milissegundos por minuto simulado */
  ritmo: number;
  /** pênalti em andamento: "tensao" (batedor na bola) e depois "resultado" (gol, defendeu ou fora) */
  suspense?: { ev: EventoPartida; etapa: 'tensao' | 'resultado' };
  /** minutos cujos lances (gol, pênalti) já foram tocados e anunciados */
  anunciado?: number;
}

export interface App {
  jogo: EstadoJogo | null;
  previa: EstadoJogo | null;
  tela: Tela;
  dialogo: Dialogo | null;
  jogadorSel: number | null;
  ordem: { col: string; asc: boolean } | null;
  mensagem: string;
  /** jogo solo: técnico demitido pelo saldo do caixa, com os clubes que o querem (só na memória, enquanto o diálogo está aberto) */
  demissao: { saldo: number; de: string; propostas: number[]; /** convite por bom desempenho (o técnico pode recusar e ficar), não demissão */ convite?: boolean } | null;
  novo: { clubeId: number; nome: string; aleatorio: boolean; divisao?: 'a' | 'b'; copa?: boolean; libertadores?: boolean; /** liga estrangeira escolhida (id de ligas-mundo.ts); ausente = Brasileirão */ ligaMundo?: string; /** true: só o Brasileirão (ou só a liga estrangeira escolhida), sem as outras ligas do mundo no save */ semMundo?: boolean };
  tabelas: { painel: 'estatisticas' | 'rodadas'; rodada: number; campeonato?: 'a' | 'b' | 'copa' | 'lib' | 'sul'; /** temporada que as Tabelas mostram (ausente = a atual) */ temporada?: number; aba?: 'nacional' | 'internacional'; pais?: string; liga?: string; clubeLiga?: string; /** rodada (0-based) do calendário da liga estrangeira mostrada na aba internacional */ rodadaMundo?: number };
  /** `externo`: índice de um clube da outra divisão (só consulta), quando selecionado */
  times: { clubeId: number | null; jogadorId: number | null; externo?: number; /** país e campeonato da lista (ausentes = Brasil e a divisão do técnico) */ pais?: string; liga?: string; /** clube de liga estrangeira escolhido (só consulta) */ mundo?: string };
  juniores: { clubeId: number | null; jogadorId: number | null; apelido: string };
  historia: { aba: 'campeoes' | 'trofeus'; clubeId: number | null };
  /** leilão online: lances digitados por leilão e dados do leilão que o usuário vai abrir */
  leilao: { valores: Record<number, string>; preco: string; duracao: string };
  procura: { filtros: FiltrosProcura; resultados: ResultadoProcura[]; sel: ResultadoProcura | null };
  negociacao: Negociacao | null;
  ofertasVenda: { jogadorId: number; ofertas: OfertaRecebida[]; soLeilao?: boolean } | null;
  escalar: EstadoEscalar | null;
  /** janela de substituições da partida online pausada */
  pausa: EstadoPausa | null;
  partida: PartidaUi | null;
  /** mini-game ao sofrer pênalti (experimento): você escolhe o canto do goleiro durante o suspense */
  minigame: boolean;
  /** clicar fora da janela (no fundo escuro) fecha o modal */
  fecharAoClicarFora: boolean;
  /** velocidade escolhida para as partidas ao vivo: 1 = normal, 3 = rápida, 0 = só o resultado */
  velocidade: 0 | 1 | 3;
  /** sessão do campeonato online (null = jogo solo) */
  online: SessaoOnline | null;
}

export const FILTROS_VAZIOS = (): FiltrosProcura => ({
  nome: '',
  posicao: '',
  lado: '',
  forcaMin: 1,
  forcaMax: 100,
  idadeMin: 16,
  idadeMax: 45,
  car1: -1,
  car2: -1,
  valor: '',
  livres: false,
  campeonato: 'br',
});

export const app: App = {
  jogo: null,
  previa: null,
  tela: 'inicio',
  dialogo: null,
  jogadorSel: null,
  ordem: null,
  mensagem: '',
  demissao: null,
  novo: { clubeId: 0, nome: 'Técnico', aleatorio: false },
  tabelas: { painel: 'estatisticas', rodada: 0 },
  times: { clubeId: null, jogadorId: null },
  juniores: { clubeId: null, jogadorId: null, apelido: '' },
  historia: { aba: 'campeoes', clubeId: null },
  leilao: { valores: {}, preco: '', duracao: '30' },
  procura: { filtros: FILTROS_VAZIOS(), resultados: [], sel: null },
  negociacao: null,
  ofertasVenda: null,
  escalar: null,
  pausa: null,
  partida: null,
  velocidade: 1,
  minigame: true,
  fecharAoClicarFora: true,
  online: null,
};

export function jogoAtual(): EstadoJogo {
  if (!app.jogo) throw new Error('nenhum jogo em andamento');
  return app.jogo;
}

/** A temporada só "acabou" para quem olha depois do apito final: online, a última rodada já está simulada, mas ainda está sendo jogada. */
export function temporadaAcabou(e: EstadoJogo): boolean {
  return e.rodada >= e.calendario.length && !app.online?.visao?.sala.aoVivo;
}

export function humano(): Clube {
  const j = jogoAtual();
  return clubePorId(j, j.humanoId);
}

/** Há um jogo salvo no navegador (sabido no início e atualizado a cada gravação); a tela inicial usa para ligar o botão. */
let saveExiste = false;
export function existeSaveLocal(): boolean {
  return saveExiste;
}

let gravacaoAgendada: number | undefined;
let gravando: Promise<void> = Promise.resolve();
/** Chamado depois de cada gravação do jogo solo (o selo do servidor se pendura aqui; ver selo.ts). */
let ganchoSalvou: (() => void) | null = null;
export function definirGanchoSalvou(f: () => void): void {
  ganchoSalvou = f;
}

async function gravarAgora(): Promise<void> {
  gravacaoAgendada = undefined;
  if (!app.jogo || app.online) return;
  const texto = salvarJson(app.jogo);
  // uma gravação por vez, na ordem: a última sempre vence
  gravando = gravando
    .catch(() => undefined)
    .then(() => gravarSave(texto))
    .then(() => {
      saveExiste = true;
      ganchoSalvou?.();
    })
    .catch(() => {
      app.mensagem = 'Não foi possível salvar no navegador (armazenamento cheio ou bloqueado). Use Opções > Exportar save.';
    });
  await gravando;
}

/**
 * Salva o jogo no navegador. Várias chamadas seguidas (cada ação do jogo chama) viram uma gravação só, logo depois, para não
 * transformar o estado inteiro em texto a cada clique. Ao fechar ou esconder a aba, grava na hora.
 */
export function salvarLocal(): void {
  if (!app.jogo || app.online) return;
  saveExiste = true;
  if (gravacaoAgendada !== undefined) return;
  gravacaoAgendada = window.setTimeout(() => void gravarAgora(), 250);
}

/** Grava já o que estiver pendente (usado ao sair da página e antes de ações que precisam do save no disco). */
export function salvarLocalAgora(): Promise<void> {
  if (gravacaoAgendada === undefined) return gravando;
  window.clearTimeout(gravacaoAgendada);
  return gravarAgora();
}

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => void salvarLocalAgora());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void salvarLocalAgora();
  });
}

/** Joga fora o save do navegador (e qualquer gravação pendente): ele não pôde ser usado. */
export async function descartarSaveLocal(): Promise<void> {
  if (gravacaoAgendada !== undefined) window.clearTimeout(gravacaoAgendada);
  gravacaoAgendada = undefined;
  saveExiste = false;
  await apagarSave();
}

export async function carregarLocal(): Promise<EstadoJogo | null> {
  try {
    await salvarLocalAgora(); // se há gravação pendente, ela entra antes de ler
    const t = await lerSave();
    saveExiste = t !== null;
    if (!t) return null;
    try {
      return carregarJson(t);
    } catch (erro) {
      // save do navegador corrompido ou de outra versão: não serve e não pode ficar travando a abertura
      console.warn('save do navegador inválido, descartado:', erro);
      await descartarSaveLocal();
      return null;
    }
  } catch {
    return null;
  }
}

export function carregarPreferencias(): void {
  try {
    const p = JSON.parse(localStorage.getItem(CHAVE_PREFS) ?? '{}') as { velocidade?: number; som?: boolean; minigame?: boolean; fecharFora?: boolean };
    if (p.velocidade === 0 || p.velocidade === 1 || p.velocidade === 3) app.velocidade = p.velocidade;
    if (p.minigame === false) app.minigame = false;
    if (p.fecharFora === false) app.fecharAoClicarFora = false;
    if (p.som === false) definirSom(false);
  } catch {
    /* sem preferências salvas */
  }
}

export function salvarPreferencias(): void {
  try {
    localStorage.setItem(CHAVE_PREFS, JSON.stringify({ velocidade: app.velocidade, som: somLigado(), minigame: app.minigame, fecharFora: app.fecharAoClicarFora }));
  } catch {
    /* ignorar */
  }
}

export function erroTexto(erro: unknown): string {
  return erro instanceof Error ? erro.message : String(erro);
}
